import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Play, Youtube, MoveHorizontal } from "lucide-react";
import { scrollToId } from "@/lib/api";

const works = [
  {
    title: "Camera da letto di lusso",
    tag: "Ristrutturazione completa",
    img: "/portfolio/camera-lusso.webp",
    tall: true,
  },
  {
    title: "Camera da letto",
    tag: "Ristrutturazione completa",
    img: "/portfolio/camera-da-letto.webp",
    tall: true,
  },
  {
    title: "Cucina e zona pranzo",
    tag: "Bagni & cucine",
    img: "/portfolio/cucina-zona-pranzo.webp",
    tall: true,
  },
  {
    title: "Ristrutturazione in corso",
    tag: "Opere edili — in corso d'opera",
    img: "/portfolio/demolizione.webp",
    tall: true,
  },
  {
    title: "Riqualificazione esterni",
    tag: "Opere esterne — in corso d'opera",
    img: "/portfolio/cortile.webp",
    tall: true,
  },
  {
    title: "Demolizioni e opere in corso",
    tag: "Opere edili — in corso d'opera",
    img: "/portfolio/cantiere-demolizione.webp",
    tall: true,
  },
  {
    title: "Planimetria di progetto",
    tag: "Progettazione",
    img: "/portfolio/planimetria.webp",
    tall: true,
  },
];

const BeforeAfterSlider = () => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      update(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const start = (e) => {
    dragging.current = true;
    update(e.touches ? e.touches[0].clientX : e.clientX);
  };

  return (
    <div
      ref={ref}
      onMouseDown={start}
      onTouchStart={start}
      data-testid="before-after-slider"
      className="relative w-full aspect-[4/5] overflow-hidden select-none cursor-ew-resize touch-none"
    >
      <img
        src="/portfolio/bagno-dopo.webp"
        alt="Bagno dopo la ristrutturazione"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <span className="absolute top-4 right-4 z-20 font-oswald uppercase tracking-[0.2em] text-xs px-4 py-2 bg-gold text-ink">Dopo</span>
      <div className="absolute inset-0 pointer-events-none" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src="/portfolio/bagno-prima.webp"
          alt="Bagno prima della ristrutturazione"
          draggable={false}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-4 left-4 font-oswald uppercase tracking-[0.2em] text-xs px-4 py-2 bg-ink/80 text-white border border-white/20">Prima</span>
      </div>
      <div className="absolute top-0 bottom-0 z-10 w-0.5 bg-gold pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-gold text-ink flex items-center justify-center shadow-xl">
          <MoveHorizontal className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const Frame = ({ work, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay: (index % 2) * 0.12, ease: [0.76, 0, 0.24, 1] }}
    className="group"
    data-testid={`portfolio-item-${index + 1}`}
  >
    <div className="bg-surface border border-white/10 p-2">
      <div className={`overflow-hidden ${work.tall ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
        <img
          src={work.img}
          alt={work.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-105"
        />
      </div>
    </div>
    <div className="mt-5">
      <h3 className="font-display text-xl lg:text-2xl group-hover:text-gold transition-colors duration-300">{work.title}</h3>
      <p className="font-oswald uppercase text-xs tracking-[0.25em] text-muted-foreground mt-2">
        {work.tag}
        {work.year ? ` — ${work.year}` : ""}
      </p>
    </div>
  </motion.article>
);

export const Portfolio = () => (
  <section id="lavori" className="py-28 lg:py-40 bg-surface border-y border-white/10" data-testid="portfolio-section">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        className="mb-20 lg:mb-28 flex flex-wrap items-end justify-between gap-8"
      >
        <div>
          <p className="font-oswald text-gold uppercase tracking-[0.35em] text-sm mb-6">Portfolio</p>
          <h2 className="font-oswald uppercase text-3xl md:text-5xl tracking-[0.08em] leading-tight" data-testid="portfolio-title">
            Lavori che <span className="text-gold">parlano da soli</span>
          </h2>
        </div>
        <button
          onClick={() => scrollToId("#preventivo")}
          data-testid="portfolio-cta"
          className="group font-oswald uppercase tracking-[0.15em] text-sm text-muted-foreground hover:text-gold transition-colors duration-300 flex items-center gap-2"
        >
          Il prossimo potrebbe essere il tuo
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </button>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {works.map((w, i) => (
          <Frame key={w.img} work={w} index={i} />
        ))}
        <motion.article
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="group"
          data-testid="portfolio-before-after"
        >
          <div className="bg-surface border border-white/10 p-2">
            <BeforeAfterSlider />
          </div>
          <div className="mt-5">
            <h3 className="font-display text-xl lg:text-2xl group-hover:text-gold transition-colors duration-300">Bagno — Prima / Dopo</h3>
            <p className="font-oswald uppercase text-xs tracking-[0.25em] text-muted-foreground mt-2">
              Ristrutturazione bagno · trascina per confrontare
            </p>
          </div>
        </motion.article>
      </div>

      <motion.a
        href="https://www.youtube.com/watch?v=wXAvfHd-PUk&t=79s"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        className="group block mt-20 lg:mt-28 max-w-3xl mx-auto"
        data-testid="portfolio-video-series"
      >
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <Youtube className="w-5 h-5 text-gold" />
          <span className="font-oswald uppercase tracking-[0.3em] text-sm text-gold">Da un Magazzino ad un Appartamento di Lusso</span>
          <span className="font-oswald uppercase tracking-[0.2em] text-xs text-muted-foreground">— Segui il cantiere passo dopo passo in questa breve serie su YouTube realizzata da un nostro cliente</span>
        </div>
        <div className="bg-ink border border-white/10 p-2 lg:p-3">
          <div className="relative overflow-hidden aspect-video">
            <img
              src="https://img.youtube.com/vi/wXAvfHd-PUk/maxresdefault.jpg"
              alt="Da Magazzino ad Appartamento di Lusso — serie video della ristrutturazione completa"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/15 transition-colors duration-500" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-gold text-ink flex items-center justify-center transition-transform duration-500 group-hover:scale-110 shadow-2xl">
                <Play className="w-8 h-8 lg:w-10 lg:h-10 fill-ink ml-1" />
              </span>
            </div>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent px-6 lg:px-10 pt-16 pb-6 lg:pb-8">
              <p className="text-sm lg:text-base text-white/80 flex items-center gap-2">
                Una ristrutturazione completa raccontata dal vivo — guarda il primo episodio
                <ArrowUpRight className="w-4 h-4 text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </p>
            </div>
          </div>
        </div>
      </motion.a>
    </div>
  </section>
);
